<template>
  <div class="upload-file">
    <el-upload :multiple="multiple" :action="exact_url ?? (baseUrl + uploadImgUrl)" :data="data"
      :before-upload="handleBeforeUpload" :file-list="fileList" :limit="limit" :method="method"
      :on-error="handleUploadError" :on-exceed="handleExceed" @="events" :="uploadOpt"
      :on-success="success ?? handleUploadSuccess" :show-file-list="true" class="upload-file-uploader"
      ref="fileUploadRef">
      <slot name="btn">
        <slot name="btn-prefix" /><el-button type="primary">{{ text }}</el-button>
        <slot name="btn-suffix" />
      </slot>
    </el-upload>
    <div class="el-upload__tip" v-if="showTip">
      请上传
      <template v-if="fileSize"> 大小不超过 <b style="color: var(--el-color-danger)">{{ fileSize }}MB</b> </template>
      <template v-if="fileType.length"> 格式为 <b style="color: var(--el-color-danger)">{{ fileType.join("/") }}</b> </template>
      的文件
    </div>
    <transition-group class="upload-file-list el-upload-list el-upload-list--text" name="el-fade-in-linear" tag="ul">
      <div :key="file.uid" class="el-upload-list__item ele-upload-list__item-content cm-ellipsis-line-one"
        v-for="(file, index) in fileList">
        <el-link :href="`${baseUrl}${file.url}`" class="cm-ellipsis-line-one" :underline="false" target="_blank">
          <span class="el-icon-document">
            <div class="cm-ellipsis-line-one">{{ getFileName(file.name) }}</div>
          </span>
        </el-link>
        <div class="ele-upload-list__item-content-action">
          <el-button type="text" class="cm-link-danger" size="small" @click="handleDelete(index)">删除</el-button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { getBaseUrl } from '@/common/const/api-base-url'
import { getDataFromPath } from '@/utils/tool'
import { ElMessage } from 'element-plus'
import { ref, computed, watch, defineEmits, defineProps, getCurrentInstance } from 'vue'
const props = defineProps({
  modelValue: [String, Object, Array],
  events: {
    type: Object,
    default: () => ({})
  },
  uploadOpt: {
    type: Object,
    default: () => ({})
  },
  success: {
    type: Function,
    default: () => { }
  },
  // 附属参数
  data: {
    type: Object,
    default: () => ({})
  },
  url: {
    type: String,
    default: ''
  },
  exact_url: {
    type: String,
    default: ''
  },
  method: {
    type: String,
    default: 'post'
  },
  multiple: {
    type: Boolean,
    default: true
  },
  multiple: {
    type: Boolean,
    default: true
  },
  text: {
    type: String,
    default: '选取文件'
  },
  // 文件数量限制
  limit: {
    type: Number,
    default: 5,
  },
  // 大小限制(MB)
  fileSize: {
    type: Number,
    default: 5,
  },
  // 文件类型, 例如["doc", "xls", "ppt", "txt", "pdf"],
  fileType: {
    type: Array,
    default: () => ["doc", "xls", "ppt", "txt", "pdf"],
  },
  isShowTip: {
    type: Boolean,
    default: true
  },
  path: {
    type: String,
    default: 'url'
  },
  headers: {
    type: Object,
    default: () => ({})
  },
})

const emit = defineEmits(['success', 'update:modelValue', 'beforeUpload', 'uploadError'])
const number = ref(0)
const fileUploadRef = ref()
const uploadList = ref([])
const dialogImageUrl = ref(props.url)
const dialogVisible = ref(false)
const baseUrl = computed(() => getBaseUrl())
const uploadImgUrl = ref('')
const fileList = ref([])
const showTip = computed(
  () => props.isShowTip && (props.fileType || props.fileSize)
)
watch(() => props.url, (nvUrl) => {
  nvUrl && (uploadImgUrl.value = nvUrl)
}, {
  immediate: true
})
watch(() => props.modelValue, val => {
  if (val) {
    // 首先将值转为数组
    const list = Array.isArray(val) ? val : props.modelValue.split(',')
    // 然后将数组转为对象数组
    fileList.value = list.map((item) => {
      if (typeof item === 'string') {
        item = { name: item, url: item }
      }
      return item
    })
  } else {
    fileList.value = []
    return []
  }
}, { deep: true, immediate: true })

// 上传前loading加载
const handleBeforeUpload = (file) => {
  return new Promise((resolve, reject) => {
    if (props.fileType.length) {
      const fileName = file.name.split('.');
      const fileExt = fileName[fileName.length - 1];
      const isTypeOk = props.fileType.indexOf(fileExt) >= 0;
      if (!isTypeOk) {
        ElMessage.error(`文件格式不正确, 请上传${props.fileType.join('/')}格式文件!`)
        emit('beforeUpload', file, resolve, reject, false)
        return false;
      }
    }
    // 校检文件大小
    if (props.fileSize) {
      const isLt = file.size / 1024 / 1024 < props.fileSize;
      if (!isLt) {
        ElMessage.error(`上传文件大小不能超过 ${props.fileSize} MB!`)
        emit('beforeUpload', file, resolve, reject, false)
        return false;
      }
    }
    emit('beforeUpload', file, resolve, reject, true)
    number.value++;
    return true;
  })
}

// 文件个数超出
const handleExceed = () => {
  ElMessage.error(`上传文件数量不能超过 ${props.limit} 个!`)
}

// 上传成功回调
const handleUploadSuccess = (res, file) => {
  const url = getDataFromPath(res, props.path || 'url')
  if (res.resp_common.msg === 'ok') {
    uploadList.value.push({ name: url, url: url })
    ElMessage.success('上传成功')
    emit('success')
    uploadedSuccessfully()
  } else {
    number.value--
    ElMessage.error('上传失败')
    fileUploadRef.value.handleRemove(file)
    uploadedSuccessfully()
  }
}

// 删除图片
const handleDelete = (file) => {
  const fileIndex = fileList.value.map(f => f.name).indexOf(file.name)
  if (fileIndex > -1 && uploadList.value.length === number.value) {
    fileList.value.splice(fileIndex, 1)
    emit('update:modelValue', listToString(fileList.value))
    return false
  }
  return true
}

// 上传结束处理
const uploadedSuccessfully = () => {
  if (number.value > 0 && uploadList.value.length === number.value) {
    fileList.value = fileList.value.filter(f => f.url !== undefined).concat(uploadList.value)
    uploadList.value = []
    number.value = 0
    emit('update:modelValue', listToString(fileList.value))
  }
}

// 上传失败
const handleUploadError = () => {
  ElMessage.error('文件上传失败')
  emit('uploadError')
}

// 预览
const handlePictureCardPreview = (file) => {
  dialogImageUrl.value = file.url
  dialogVisible.value = true
}

// 对象转成指定字符串分隔
const listToString = (list, separator) => {
  let strs = ''
  separator = separator || ','
  for (const i in list) {
    if (undefined !== list[i]?.url && list[i]?.url?.indexOf?.('blob:') !== 0) {
      strs += list[i]?.url?.replace?.(baseUrl.value, '') + separator
    }
  }
  return strs !== '' ? strs.substr(0, strs.length - 1) : ''
}
defineExpose({
  fileUploadRef,
})
</script>

<style scoped lang='scss'>
.upload-file-uploader {
  width: 100%;
  margin-bottom: 5px;
  box-sizing: border-box;
}

.upload-file-list .el-upload-list__item {
  display: inline-block;
  border: 1px solid var(--el-border-color-light, #e4e7ed);
  line-height: 2;
  margin-bottom: 10px;
  position: relative;
}
:deep(.el-upload-list){
  line-height: 0;
  margin-top: 5px;
}
:deep(.el-upload-list__item) {
  display: inline-block;
  box-sizing: border-box;

  &:first-child {
    margin-top: 0;
  }

  & .el-upload-list__item-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
  }
}

.upload-file-list .ele-upload-list__item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: inherit;
}

.ele-upload-list__item-content-action .el-link {
  margin-right: 10px;
}

:deep(.hide) .el-upload--picture-card {
  display: none !important;
}

.el-upload__tip {
  line-height: 1rem;
  margin-top: 0;
}
</style>
